import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Eye, Loader2, Clock, Video, Image as ImageIcon, FilmIcon, AlertCircle, CheckCircle2, XCircle, Pen, MessageSquare, MessageCircle, Plus, Trash2, MoreVertical, Sparkles, Coins, RotateCw, ChevronUp, ChevronDown, X, Pause, Play, Scissors, AlertTriangle, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { api, getApiErrorMessage } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@/components/EmptyState';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { findCatalogModel, useModelCatalog } from '@/hooks/useModelCatalog';

type ContentStatus = 'draft' | 'approved' | 'awaiting_compilation' | 'processing' | 'completed' | 'failed';
type SceneStatus = 'draft' | 'processing' | 'completed' | 'failed' | 'awaiting_compilation';

// Use the backend scene interface
interface BackendScene {
  id: number;
  number: number;
  start_time: number;
  end_time: number;
  description: string;
  dialogue?: string;
  duration: number;
  audio_duration?: number;
  visual_duration?: number;
  media_type: string;
  visual_url?: string;
  audio_url?: string;
  audio_updated_at: string;
  status: SceneStatus;
  visual_type: 'auto' | 'video' | 'image' | 'motion';
  resolved_visual_type?: 'video' | 'image' | 'motion';
  visual_metadata?: Record<string, unknown>;
  visual_model?: string;
  search_keywords?: string;
}

interface StoryboardData {
  id: number;
  created_at: string;
  updated_at: string;
  duration: number;
  language: string;
  platform: string;
  status: ContentStatus;
  thumbnail_url: string | undefined;
  title: string;
  topic: string;
  video_url: string | undefined;
  voice: string;
  scenes: BackendScene[];
}

interface EditingState {
  type: 'visual' | 'dialogue' | 'duration' | null;
  sceneId: number | null;
}

// Module-level ref so any audio element can stop any other that's currently playing
let activeAudioEl: HTMLAudioElement | null = null;

export function stopActiveAudio() {
  if (activeAudioEl) {
    activeAudioEl.pause();
    activeAudioEl = null;
  }
}

function CustomAudioPlayer({ audioUrl, showMilliseconds }: { audioUrl: string; showMilliseconds?: boolean }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handlePlay = () => {
      if (activeAudioEl && activeAudioEl !== audio) activeAudioEl.pause();
      activeAudioEl = audio;
    };
    const handlePause = () => {
      if (activeAudioEl === audio) activeAudioEl = null;
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      if (activeAudioEl === audio) activeAudioEl = null;
    };
  }, []);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 1000);
    return showMilliseconds
      ? `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
      : `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full flex flex-col gap-1">
      <audio ref={audioRef} src={audioUrl} controls className="w-full" style={{ maxHeight: '40px' }} />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
}

const getStatusColor = (status: SceneStatus) => {
  switch (status) {
    case 'completed': return 'bg-green-500';
    case 'processing': return 'bg-blue-500';
    case 'failed': return 'bg-red-500';
    case 'draft': default: return 'bg-blue-200';
  }
};

export function StoryboardPage() {
  const { data: modelCatalog } = useModelCatalog();
  const availableModels = modelCatalog?.groups.flatMap(group => group.models) || [];
  const getSceneModel = (scene: BackendScene) =>
    availableModels.find(model => model.id === scene.visual_model);
  const isStockScene = (scene: BackendScene) =>
    getSceneModel(scene)?.media_type === 'stock';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [selectedScene, setSelectedScene] = useState<BackendScene | null>(null);
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [storyboard, setStoryboard] = useState<StoryboardData | null>(null);
  const [editingState, setEditingState] = useState<EditingState>({ type: null, sceneId: null });
  const [editedDialogue, setEditedDialogue] = useState<string>("");
  const [isRegeneratingAudio, setIsRegeneratingAudio] = useState(false);
  const [showCombineConfirm, setShowCombineConfirm] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [isCalculatingCost, setIsCalculatingCost] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [editedDescription, setEditedDescription] = useState<string>("");
  const [isRegeneratingVisual, setIsRegeneratingVisual] = useState(false);
  const [showTweakDialog, setShowTweakDialog] = useState(false);
  const [tweakFeedback, setTweakFeedback] = useState("");
  const [selectedSceneIds, setSelectedSceneIds] = useState<number[]>([]);
  const [isTweaking, setIsTweaking] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerateText, setRegenerateText] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [editedDuration, setEditedDuration] = useState<number>(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sceneToDelete, setSceneToDelete] = useState<BackendScene | null>(null);
  const [showNewSceneDialog, setShowNewSceneDialog] = useState(false);
  const [isGeneratingScene, setIsGeneratingScene] = useState(false);
  const [isDeletingScene, setIsDeletingScene] = useState(false);
  const [newScenePosition, setNewScenePosition] = useState<number | null>(null);
  const [newSceneDescription, setNewSceneDescription] = useState("");
  const [creditCostInfo, setCreditCostInfo] = useState<{
    creditCost: number;
    scenes: number;
    visualModel: string;
  } | null>(null);
  const [isRegeneratingMedia, setIsRegeneratingMedia] = useState(false);
  const [isShorteningDialogue, setIsShorteningDialogue] = useState(false);
  const [isShorteningAllAudio, setIsShorteningAllAudio] = useState(false);
  const [isRegenerateMediaDialogOpen, setIsRegenerateMediaDialogOpen] = useState(false);
  const [sceneToRegenerate, setSceneToRegenerate] = useState<BackendScene | null>(null);
  const [regeneratingSceneIds, setRegeneratingSceneIds] = useState<number[]>([]);
  const [isCalculatingRegenerateCost, setIsCalculatingRegenerateCost] = useState(false);
  const [regenerateCreditInfo, setRegenerateCreditInfo] = useState<{
    creditCost: number;
    visualModel: string;
  } | null>(null);
  const [totalDuration, setTotalDuration] = useState<number>(0);

  // Duration options for video/motion and images. Floor is 5s (no scene
  // shorter than that); video/motion runs 5–15s to match the backend pacing
  // range, images allow up to 20s.
  const videoDurationOptions = Array.from({length: 11}, (_, i) => i + 5); // 5–15
  const imageDurationOptions = Array.from({length: 16}, (_, i) => i + 5); // 5–20

  const { data: storyboardData, isLoading, error, refetch } = useQuery({
    queryKey: ['storyboard', id],
    queryFn: async () => {
      const response = await api.get(`/storyboard/content/${id}/storyboard`);
      console.log('Raw storyboard response:', JSON.stringify(response.data, null, 2));
      console.log('Scene data example:', JSON.stringify(response.data.scenes[0], null, 2));
      setStoryboard(response.data);
      return response.data;
    },
    retry: 1,
    placeholderData: (previousData) => previousData,
    // Auto-poll while any scene is still rendering (motion/stock regenerate and
    // Replicate are all async now), so they update to completed without a manual
    // refresh. Stops once nothing is processing.
    refetchInterval: (query) => {
      const scenes = query.state.data?.scenes as { status?: string }[] | undefined;
      return scenes?.some((s) => s.status === 'processing') ? 4000 : false;
    },
  });

  // Update helper function to get the current status
  const getContentStatus = (): ContentStatus | undefined => {
    return storyboard?.status;
  };

  // Lock editing once compilation is actively running or done
  const isContentLocked = (): boolean => {
    const s = storyboard?.status;
    return s === 'processing' || s === 'completed';
  };

  /** True when every scene has finished media (matches backend drift where parent status may lag). */
  const areAllScenesMediaComplete = (): boolean => {
    if (!storyboard?.scenes?.length) return false;
    return storyboard.scenes.every(
      (s) => s.status === 'completed' || s.status === 'awaiting_compilation'
    );
  };

  /** Final stitch when content is awaiting compilation, or all scene media is ready (combine flow will PATCH parent status first if needed). */
  const canCreateFinalVideo = (): boolean => {
    if (!storyboard || storyboard.video_url) return false;
    const status = storyboard.status;
    if (status === 'processing') return false;
    if (status === 'awaiting_compilation') return true;
    return areAllScenesMediaComplete();
  };

  const getAudioTargetDuration = (scene: BackendScene): number => {
    return scene.resolved_visual_type === 'motion'
      ? scene.duration
      : (scene.visual_duration ?? scene.duration);
  };

  const scenesNeedingAudioShortening = storyboard?.scenes?.filter(
    (scene) =>
      Boolean(scene.dialogue) &&
      Boolean(scene.audio_duration) &&
      (scene.audio_duration ?? 0) > getAudioTargetDuration(scene)
  ) ?? [];

  // Calculate total duration whenever storyboard changes
  useEffect(() => {
    if (storyboard?.scenes) {
      const newTotalDuration = storyboard.scenes.reduce((total, scene) => total + scene.duration, 0);
      setTotalDuration(newTotalDuration);
    }
  }, [storyboard]);

  // Replace all conditional status checks
  useEffect(() => {
    if (storyboard) {
      const status = getContentStatus();
      console.log('Current content status:', status);
    }
  }, [storyboard]);

  const handlePreview = (scene: BackendScene) => {
    setSelectedScene(scene);
    setShowPreview(true);
    
    // Try to unmute the video after a short delay (for browsers that restrict autoplay with sound)
    if (scene.media_type === 'video') {
      setTimeout(() => {
        const videoElement = document.querySelector('video');
        if (videoElement) {
          videoElement.muted = false;
          videoElement.play().catch(err => {
            console.log("Could not autoplay with sound:", err);
          });
        }
      }, 500);
    }
  };

  const downloadSceneMedia = async (scene: BackendScene, mediaType: 'audio' | 'video') => {
    try {
      const response = await api.get<{ download_url: string; filename: string }>(
        `/storyboard/scene/${scene.id}/download/${mediaType}`
      );
      const a = document.createElement('a');
      a.href = response.data.download_url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: 'Download failed',
        description: getApiErrorMessage(error, `Could not download scene ${mediaType}.`),
        variant: 'destructive',
      });
    }
  };

  const handleDownloadAudio = async (scene: BackendScene) => {
    if (!scene.audio_url) return;
    await downloadSceneMedia(scene, 'audio');
  };

  const handleDownloadVideo = async (scene: BackendScene) => {
    if (!scene.visual_url) return;
    await downloadSceneMedia(scene, 'video');
  };

  const handleDownloadFinalVideo = async () => {
    if (!storyboard?.video_url) return;
    try {
      const response = await api.get<{ download_url: string; filename: string }>(
        `/storyboard/content/${storyboard.id}/download`
      );
      const a = document.createElement('a');
      a.href = response.data.download_url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: 'Download failed',
        description: getApiErrorMessage(error, 'Could not download the final video.'),
        variant: 'destructive',
      });
    }
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error('Video error:', e.currentTarget.error);
    console.log('Video URL:', e.currentTarget.src);
    toast({
      title: "Video Error",
      description: `Failed to load video: ${e.currentTarget.error?.message || 'Unknown error'}`,
      variant: "destructive",
    });
  };

  const handleVideoLoad = () => {
    console.log('Video loaded successfully');
  };

  const handleCombineScenes = async () => {
    if (!storyboard?.id) {
      toast({
        title: "Error",
        description: "Storyboard ID is missing",
        variant: "destructive",
      });
      return;
    }

    if (!id) {
      toast({
        title: "Error",
        description: "Content ID is missing",
        variant: "destructive",
      });
      return;
    }
    
    setIsProcessing(true);
    setShowCombineConfirm(false);
    
    try {
      const status = storyboard.status;
      if (
        status !== 'awaiting_compilation' &&
        status !== 'processing' &&
        status !== 'completed' &&
        areAllScenesMediaComplete()
      ) {
        await api.patch(`/content/${id}`, { status: 'awaiting_compilation' });
        setStoryboard((prev) => (prev ? { ...prev, status: 'awaiting_compilation' } : null));
      }

      await api.post(`/storyboard/content/${storyboard.id}/combine-scenes`);
      
      toast({
        title: "Processing Started",
        description: "Your scenes are being combined into a final video. This process may take several minutes. You'll be notified when it's ready.",
        duration: 5000,
      });
      
      // Update the storyboard data to reflect processing state
      setStoryboard((prev: StoryboardData | null) => {
        if (!prev) return null;
        return {
          ...prev,
          status: "processing"
        };
      });
      
      // Start polling for status updates
      const pollInterval = setInterval(async () => {
        const response = await api.get(`/storyboard/content/${storyboard.id}/storyboard`);
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          clearInterval(pollInterval);
          setStoryboard(response.data);
          setIsProcessing(false);
          
          if (response.data.status === 'completed') {
            toast({
              title: "Video Ready!",
              description: "Your final video has been created successfully.",
              variant: "default",
            });
          } else {
            toast({
              title: "Processing Failed",
              description: "There was an error creating your video. Please try again.",
              variant: "destructive",
            });
          }
        }
      }, 15000); // Poll every 15 seconds instead of 5 seconds
      
      // Cleanup interval on component unmount
      return () => clearInterval(pollInterval);
      
    } catch (error) {
      console.error('Error combining scenes:', error);
      
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to combine scenes"),
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const handleEditDialogue = (scene: BackendScene) => {
    setEditingState({ type: 'dialogue', sceneId: scene.id });
    setEditedDialogue(scene.dialogue || "");
  };

  const handleShortenDialogue = async (scene: BackendScene) => {
    if (!scene.audio_duration || !scene.dialogue) return;
    setIsShorteningDialogue(true);
    try {
      const response = await api.post('/ai/shorten-dialogue', {
        dialogue: editedDialogue || scene.dialogue,
        audio_duration: scene.audio_duration,
        scene_duration: scene.duration,
      });
      setEditedDialogue(response.data.shortened_dialogue);
      toast({ title: "Dialogue shortened", description: "Review the shortened text before saving." });
    } catch {
      toast({ title: "Error", description: "Failed to shorten dialogue", variant: "destructive" });
    } finally {
      setIsShorteningDialogue(false);
    }
  };

  const handleShortenAllAudio = async () => {
    if (!storyboard?.id || scenesNeedingAudioShortening.length === 0) return;

    setIsShorteningAllAudio(true);
    try {
      const response = await api.post(`/storyboard/content/${storyboard.id}/shorten-audio`, {
        scene_ids: scenesNeedingAudioShortening.map(scene => scene.id),
      });
      const sceneCount = response.data.scene_count ?? scenesNeedingAudioShortening.length;
      toast({
        title: "Audio shortening queued",
        description: `Shortening ${sceneCount} scene${sceneCount === 1 ? '' : 's'} and regenerating audio.`,
      });
      setTimeout(() => refetch(), 3000);
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to queue audio shortening"),
        variant: "destructive",
      });
    } finally {
      setIsShorteningAllAudio(false);
    }
  };

  const handleRenderMotion = async (scene: BackendScene) => {
    if (!storyboard) return;
    setRegeneratingSceneIds(prev => [...prev, scene.id]);
    try {
      await api.post(`/storyboard/scene/${scene.id}/regenerate-media`);
      toast({ title: "Motion re-rendering", description: "Re-rendering with saved template." });
      setTimeout(() => refetch(), 3000);
    } catch {
      toast({ title: "Error", description: "Failed to re-render motion clip", variant: "destructive" });
    } finally {
      setRegeneratingSceneIds(prev => prev.filter(id => id !== scene.id));
    }
  };

  const handleSaveDialogue = async (scene: BackendScene) => {
    if (!storyboard) return;

    setIsRegeneratingAudio(true);
    try {
      const response = await api.post(`/storyboard/scene/${scene.id}/regenerate-audio`, {
        dialogue: editedDialogue
      });

      const { audio_duration: newAudioDuration, audio_url: newAudioUrl } = response.data;

      // Update local state immediately with new dialogue, audio_duration, and audio_url
      const updatedScenes = storyboard.scenes.map(s => {
        if (s.id === scene.id) {
          return {
            ...s,
            dialogue: editedDialogue,
            audio_duration: newAudioDuration ?? s.audio_duration,
            audio_url: newAudioUrl ?? s.audio_url,
          };
        }
        return s;
      });

      setStoryboard({ ...storyboard, scenes: updatedScenes });
      setEditingState({ type: null, sceneId: null });
      setEditedDialogue("");

      toast({
        title: "Audio updated",
        description: "Your new audio is ready.",
      });

      refetch();

    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to regenerate audio"),
        variant: "destructive",
      });
    } finally {
      setIsRegeneratingAudio(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingState({ type: null, sceneId: null });
    setEditedDescription("");
    setEditedDialogue("");
    setEditedDuration(0);
  };

  const handleEditVisual = (scene: BackendScene) => {
    setEditingState({ type: 'visual', sceneId: scene.id });
    setEditedDescription(
      isStockScene(scene) ? (scene.search_keywords || '') : (scene.description || '')
    );
  };

  const handleSaveVisual = async (scene: BackendScene) => {
    if (!storyboard) return;
    
    setIsRegeneratingVisual(true);
    try {
      const payload = isStockScene(scene)
        ? { search_keywords: editedDescription }
        : { description: editedDescription };

      await api.post(`/storyboard/scene/${scene.id}/regenerate-visual`, payload);

      const updatedScenes = storyboard.scenes.map(s => {
        if (s.id === scene.id) {
          return isStockScene(scene)
            ? { ...s, search_keywords: editedDescription }
            : { ...s, description: editedDescription };
        }
        return s;
      });
      
      setStoryboard({ ...storyboard, scenes: updatedScenes });
      setEditingState({ type: null, sceneId: null });
      setEditedDescription("");
      
      toast({
        title: "Visual regeneration started",
        description: "Your new visual is being generated. The updated content may take a few moments to appear.",
      });
      
      setTimeout(() => {
        refetch();
      }, 2000);
      
    } catch (error) {
      console.error('Error regenerating visual:', error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to regenerate visual"),
        variant: "destructive",
      });
    } finally {
      setIsRegeneratingVisual(false);
    }
  };

  const handleEditDuration = (scene: BackendScene) => {
    setEditingState({ type: 'duration', sceneId: scene.id });
    setEditedDuration(scene.duration);
  };

  const handleSaveDuration = async (scene: BackendScene, newDuration: number) => {
    if (!storyboard) return;
    
    console.log('Saving duration:', { sceneId: scene.id, oldDuration: scene.duration, newDuration });
    
    try {
      await api.post(`/storyboard/scene/${scene.id}/update-duration`, {
        duration: newDuration
      });
      
      // Update the local state
      const updatedScenes = storyboard.scenes.map(s => {
        if (s.id === scene.id) {
          return { ...s, duration: newDuration };
        }
        return s;
      });
      
      // Calculate new total duration
      const newTotalDuration = updatedScenes.reduce((total, s) => total + s.duration, 0);
      
      setStoryboard({ 
        ...storyboard, 
        scenes: updatedScenes,
        duration: newTotalDuration
      });
      
      setTotalDuration(newTotalDuration);
      setEditingState({ type: null, sceneId: null });
      
      toast({
        title: "Duration Updated",
        description: `Scene ${scene.number} duration is now ${newDuration}s`,
      });
      
    } catch (error) {
      console.error('Error updating duration:', error);
      toast({
        title: "Error",
        description: "Failed to update scene duration",
        variant: "destructive",
      });
    }
  };

  const handleTweakScenes = async () => {
    if (!storyboard?.id || selectedSceneIds.length === 0) return;
    
    setIsTweaking(true);
    try {
      await api.post(`/storyboard/content/${storyboard.id}/regenerate-scenes`, {
        scene_ids: selectedSceneIds,
        text: tweakFeedback
      });
      
      toast({
        title: "Tweak Started",
        description: `Updating ${selectedSceneIds.length} scene${selectedSceneIds.length > 1 ? 's' : ''}...`,
      });
      
      setShowTweakDialog(false);
      setSelectedSceneIds([]);
      setTweakFeedback("");
      
      setTimeout(() => {
        refetch();
      }, 2000);
      
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to update scenes. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsTweaking(false);
    }
  };


  const handleRegenerateScenes = async () => {
    if (!storyboard?.id || selectedSceneIds.length === 0) return;
    
    setIsRegenerating(true);
    try {
      await api.post(`/storyboard/content/${storyboard.id}/regenerate-scenes`, {
        scene_ids: selectedSceneIds,
        text: regenerateText
      });
      
      toast({
        title: "Regeneration Started",
        description: `Regenerating ${selectedSceneIds.length} scene${selectedSceneIds.length > 1 ? 's' : ''}...`,
      });
      
      setShowRegenerateDialog(false);
      setSelectedSceneIds([]);
      setRegenerateText("");
      
      setTimeout(() => {
        refetch();
      }, 2000);
      
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to regenerate scenes. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCancelTweak = () => {
    setShowTweakDialog(false);
    setTweakFeedback("");
    setSelectedSceneIds([]);
  };

  const handleToggleMediaType = async (scene: BackendScene) => {
    if (!storyboard) return;
    
    const newType = scene.media_type === 'video' ? 'image' : 'video';
    
    try {
      await api.post(`/storyboard/scene/${scene.id}/update-media-type`, {
        media_type: newType
      });
      
      // Update the local state
      const updatedScenes = storyboard.scenes.map(s => {
        if (s.id === scene.id) {
          return { ...s, media_type: newType };
        }
        return s;
      });
      
      // Recalculate the total duration
      const newTotalDuration = updatedScenes.reduce((total, s) => total + s.duration, 0);
      
      setStoryboard({ 
        ...storyboard, 
        scenes: updatedScenes,
        duration: newTotalDuration 
      });
      
      setTotalDuration(newTotalDuration);
      
      toast({
        title: "Scene Type Updated",
        description: `Scene ${scene.number} is now a ${newType} scene`,
      });
      
      setTimeout(() => {
        refetch();
      }, 2000);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update scene type",
        variant: "destructive",
      });
    }
  };

  const handleChangeVisualType = async (scene: BackendScene, newVisualType: 'auto' | 'video' | 'image' | 'motion') => {
    const currentDisplayed = scene.resolved_visual_type || scene.visual_type || 'auto';
    if (!storyboard || newVisualType === currentDisplayed) return;

    try {
      await api.patch(`/storyboard/content/${storyboard.id}/scenes/${scene.id}`, {
        visual_type: newVisualType,
      });

      const resolvedType = newVisualType === 'auto' ? undefined : newVisualType as 'video' | 'image' | 'motion';
      const updatedScenes = storyboard.scenes.map(s =>
        s.id === scene.id ? { ...s, visual_type: newVisualType, resolved_visual_type: resolvedType } : s
      );
      setStoryboard({ ...storyboard, scenes: updatedScenes });

      if (newVisualType === 'motion') {
        // Auto-suggest template and save metadata — rendering happens after approval like all other types
        try {
          const suggestRes = await api.post('/ai/suggest-motion-template', {
            description: scene.description,
            dialogue: scene.dialogue,
            accent: '#6366f1',
          });
          const metadata = suggestRes.data.visual_metadata;

          await api.patch(`/storyboard/content/${storyboard.id}/scenes/${scene.id}`, {
            visual_metadata: metadata,
          });

          // Update local state with the suggested metadata
          const withMeta = storyboard.scenes.map(s =>
            s.id === scene.id ? { ...s, visual_metadata: metadata } : s
          );
          setStoryboard({ ...storyboard, scenes: withMeta });

          toast({ title: "Motion template set", description: `${metadata.template} — will render on approval` });
        } catch {
          toast({ title: "Couldn't suggest template", description: "Template saved as-is, will render on approval.", variant: "destructive" });
        }
      } else {
        toast({ title: "Visual type updated", description: `Scene ${scene.number} set to ${newVisualType}` });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update visual type", variant: "destructive" });
    }
  };

  const handleChangeSceneModel = async (scene: BackendScene, model: string) => {
    if (!storyboard) return;
    const selectedModel = availableModels.find(candidate => candidate.id === model);
    const visual_type = selectedModel?.media_type === 'image'
      ? 'image'
      : selectedModel?.media_type === 'motion'
        ? 'motion'
        : 'video';
    try {
      await api.patch(`/storyboard/content/${storyboard.id}/scenes/${scene.id}`, { model, visual_type });
      const updatedScenes = storyboard.scenes.map(s =>
        s.id === scene.id
          ? { ...s, visual_model: model, visual_type: visual_type as BackendScene['visual_type'], resolved_visual_type: visual_type as BackendScene['resolved_visual_type'] }
          : s
      );
      setStoryboard({ ...storyboard, scenes: updatedScenes });
    } catch {
      toast({ title: "Error", description: "Failed to update scene style", variant: "destructive" });
    }
  };

  const handleDeleteScene = async (scene: BackendScene) => {
    if (!storyboard) return;
    
    setIsDeletingScene(true);
    try {
      await api.delete(`/storyboard/scene/${scene.id}`);
      
      // Update the local state by removing the scene
      const updatedScenes = storyboard.scenes.filter(s => s.id !== scene.id);
      
      // Recalculate the total duration
      const newTotalDuration = updatedScenes.reduce((total, s) => total + s.duration, 0);
      
      setStoryboard({ 
        ...storyboard, 
        scenes: updatedScenes,
        duration: newTotalDuration
      });
      
      setTotalDuration(newTotalDuration);
      
      toast({
        title: "Scene Deleted",
        description: `Scene ${scene.number} has been deleted`,
      });
      
      setShowDeleteDialog(false);
      setSceneToDelete(null);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete scene",
        variant: "destructive",
      });
    } finally {
      setIsDeletingScene(false);
    }
  };

  const handleGenerateNewScene = async () => {
    if (!storyboard) return;
    
    setIsGeneratingScene(true);
    try {
      // If no position selected, default to end of list
      const position = newScenePosition || storyboard.scenes.length + 1;
      const is_last_scene = position === storyboard.scenes.length + 1;
      
      const response = await api.post(`/storyboard/content/${storyboard.id}/generate-scene`, {
        position,
        description: newSceneDescription || undefined,
        is_last_scene,
        previous_scene_id: position > 1 ? storyboard.scenes[position - 2]?.id : undefined,
        next_scene_id: !is_last_scene ? storyboard.scenes[position - 1]?.id : undefined
      });
      
      // Update the local state with the new scene
      const newScene = response.data;
      const updatedScenes = [...storyboard.scenes];
      updatedScenes.splice(position - 1, 0, newScene);
      
      // Update scene numbers
      const reorderedScenes = updatedScenes.map((scene, idx) => ({
        ...scene,
        number: idx + 1
      }));
      
      // Recalculate the total duration
      const newTotalDuration = reorderedScenes.reduce((total, s) => total + s.duration, 0);
      
      setStoryboard({
        ...storyboard,
        scenes: reorderedScenes,
        duration: newTotalDuration
      });
      
      setTotalDuration(newTotalDuration);
      
      toast({
        title: "New Scene Generated",
        description: "A new scene has been added to your storyboard",
      });
      
      // Reset the form
      setNewScenePosition(null);
      setNewSceneDescription("");
      setShowNewSceneDialog(false);
      
    } catch (error) {
      console.error('Error generating scene:', error);
      toast({
        title: "Error",
        description: "Failed to generate new scene",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingScene(false);
    }
  };

  const handleCalculateCreditCost = async () => {
    if (!storyboard?.id) return;
    
    setIsCalculatingCost(true);
    try {
      const response = await api.get(`/storyboard/content/${storyboard.id}/credit-cost`);
      console.log('Credit cost response:', response.data);
      
      const { creditCost, scenes, visualModel } = response.data;
      if (typeof creditCost !== 'number') {
        throw new Error('Invalid credit cost response');
      }
      
      setCreditCostInfo({ creditCost, scenes, visualModel });
      setShowApprovalDialog(true);
    } catch (error) {
      console.error('Error calculating credit cost:', error);
      toast({
        title: "Error",
        description: "Failed to calculate credit cost. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCalculatingCost(false);
    }
  };

  const handleApproveContent = async () => {
    if (!storyboard?.id) return;
    
    setIsApproving(true);
    try {
      const response = await api.post(`/storyboard/content/${storyboard.id}/approve`);
      
      // Update local state
      setStoryboard(prev => prev ? {
        ...prev,
        status: response.data.status ?? 'approved'
      } : null);
      
      setShowApprovalDialog(false);
      
      toast({
        title: "Content Approved",
        description: "Your content has been approved and is ready for compilation.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to approve content. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleRegenerateMedia = async (scene: BackendScene) => {
    try {
      // Show immediate feedback that request was registered
      setRegeneratingSceneIds(prev => [...prev, scene.id]);
      
      // If in draft or failed state, regenerate directly without confirmation
      if (scene.status === 'draft' || scene.status === 'failed') {
        toast({
          title: "Processing Request",
          description: "Sending regeneration request to server...",
        });
        
        await api.post(`/storyboard/scene/${scene.id}/regenerate-media`);
        toast({
          title: "Regeneration Started",
          description: "Your scene's media is being regenerated. This may take a few moments.",
        });
        refetch();
        return;
      }
      
      // If completed, calculate credit cost before showing confirmation dialog
      if (scene.status === 'completed') {
        setIsCalculatingRegenerateCost(true);
        try {
          // Get the credit cost using the proper endpoint
          const response = await api.get(`/storyboard/content/${storyboard?.id}/credit-cost`, {
            params: { scene_id: scene.id, regenerate: true }
          });
          
          console.log('Regenerate cost response:', response.data);
          
          const { creditCost, scenes, visualModel } = response.data;
          if (typeof creditCost !== 'number') {
            throw new Error('Invalid credit cost response');
          }
          
          setRegenerateCreditInfo({ creditCost, visualModel });
          setSceneToRegenerate(scene);
          setIsRegenerateMediaDialogOpen(true);
        } catch (error) {
          console.error('Error calculating regenerate cost:', error);
          toast({
            title: "Error",
            description: "Failed to calculate credit cost for regeneration. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsCalculatingRegenerateCost(false);
        }
        return;
      }
      
      // For any other status, this shouldn't be called as button will be disabled
      toast({
        title: "Cannot Regenerate",
        description: "Media can only be regenerated when in draft, failed, or completed states",
        variant: "destructive",
      });
      
    } catch (error) {
      console.error('Error regenerating media:', error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error, 'Failed to regenerate media'),
        variant: "destructive",
      });
    } finally {
      setRegeneratingSceneIds(prev => prev.filter(id => id !== scene.id));
    }
  };

  const handleConfirmRegenerate = async () => {
    if (!sceneToRegenerate) return;
    
    try {
      // Show immediate feedback
      setRegeneratingSceneIds(prev => [...prev, sceneToRegenerate.id]);
      toast({
        title: "Processing Request",
        description: "Sending regeneration request to server...",
      });

      await api.post(`/storyboard/scene/${sceneToRegenerate.id}/regenerate-media`);
      toast({
        title: "Regeneration Started",
        description: "Your scene's media is being regenerated. This will use credits from your account.",
      });
      setIsRegenerateMediaDialogOpen(false);
      setSceneToRegenerate(null);
      refetch();
    } catch (error) {
      console.error('Error regenerating media:', error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error, 'Failed to regenerate media'),
        variant: "destructive",
      });
    } finally {
      if (sceneToRegenerate) {
        setRegeneratingSceneIds(prev => prev.filter(id => id !== sceneToRegenerate.id));
      }
    }
  };

  // Render the individual scene with the correct status
  const renderScene = (scene: BackendScene) => {
    const isEditingVisual = editingState.type === 'visual' && editingState.sceneId === scene.id;
    const isEditingDialogue = editingState.type === 'dialogue' && editingState.sceneId === scene.id;
    const isEditingDuration = editingState.type === 'duration' && editingState.sceneId === scene.id;
    const sceneModel = findCatalogModel(modelCatalog?.groups, scene.visual_model);
    const isImageScene = sceneModel?.media_type === 'image' || scene.media_type === 'image';
    // Uploaded scenes use the user's own media — no model picker or regenerate.
    const isUpload = scene.visual_model === 'upload';

    // Prefer the model's accepted durations (e.g. Kling 5/10, Veo 5-8);
    // otherwise fall back to the flexible image/video ranges.
    const baseDurationOptions =
      sceneModel?.duration_options && sceneModel.duration_options.length > 0
        ? sceneModel.duration_options
        : isImageScene
          ? imageDurationOptions
          : videoDurationOptions;
    // Always include the scene's current duration so the dropdown never renders
    // blank for a value outside the standard set (e.g. older sub-5s scenes).
    const durationOptions = baseDurationOptions.includes(scene.duration)
      ? baseDurationOptions
      : [...baseDurationOptions, scene.duration].sort((a, b) => a - b);

    return (
      <Card key={scene.id} className="p-3 md:p-6">
        <div className="flex items-start gap-2 md:gap-4">
          <Checkbox
            checked={selectedSceneIds.includes(scene.id)}
            onCheckedChange={(checked: boolean) => {
              setSelectedSceneIds(prev => 
                checked 
                  ? [...prev, scene.id]
                  : prev.filter(id => id !== scene.id)
              );
            }}
            disabled={isContentLocked()}
            className="mt-1"
          />
          <div className="flex-1 min-w-0">
            <div className="space-y-3 md:space-y-4">
              {/* Scene Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                  <h3 className="text-base md:text-lg font-medium">Scene {scene.number}</h3>
                  {isUpload ? (
                    <span className="rounded-md border px-2 py-0.5 text-xs text-muted-foreground">
                      Uploaded
                    </span>
                  ) : (
                  <Select
                    value={scene.visual_model && scene.visual_model !== 'auto' ? scene.visual_model : ''}
                    onValueChange={(value) => handleChangeSceneModel(scene, value)}
                    disabled={isContentLocked()}
                  >
                    <SelectTrigger className="h-6 w-[120px] px-2 text-xs">
                      <SelectValue placeholder="Pick style">{sceneModel?.name}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableModels.map(model => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name}
                          <span className="ml-1 text-muted-foreground">
                            {model.credits_per_scene} cr/scene
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  )}

                  <Select
                    value={scene.duration.toString()}
                    onValueChange={(value) => {
                      const newDuration = parseInt(value, 10);
                      console.log('Duration selected:', { value, newDuration, currentDuration: scene.duration });
                      if (!isNaN(newDuration) && newDuration !== scene.duration) {
                        handleSaveDuration(scene, newDuration);
                      }
                    }}
                    disabled={isContentLocked()}
                  >
                    <SelectTrigger className="h-6 w-[70px] px-2 text-xs">
                      <SelectValue placeholder="Duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {durationOptions.map((duration) => (
                        <SelectItem key={duration} value={duration.toString()}>
                          {duration}s
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Status indicator */}
                  <div className="flex items-center gap-1">
                    <div className={`h-2 w-2 rounded-full ${getStatusColor(scene.status || 'pending')}`}></div>
                    <span className="text-xs capitalize">{(scene.status || 'pending').replace('_', ' ')}</span>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSceneToDelete(scene);
                    setShowDeleteDialog(true);
                  }}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 px-2"
                  disabled={isContentLocked()}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3 md:space-y-4">
                {/* Visual Description Section */}
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-1.5">
                    {isStockScene(scene) ? 'Search Query' : 'Visual Description'}
                  </h4>
                  {isEditingVisual ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder="Describe what should be shown in this scene..."
                        className="min-h-[100px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSaveVisual(scene)}
                          disabled={isRegeneratingVisual}
                        >
                          {isRegeneratingVisual ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Updating...
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isRegeneratingVisual}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs md:text-sm text-gray-600 line-clamp-3 md:line-clamp-none break-words">
                      {isStockScene(scene) ? (scene.search_keywords || scene.description) : scene.description}
                    </p>
                  )}
                </div>

                {/* Dialogue Section */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <h4 className="text-xs font-medium text-muted-foreground">Dialogue/Narration</h4>
                    {scene.audio_duration && scene.audio_duration > getAudioTargetDuration(scene) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1 text-xs text-amber-600 font-medium cursor-default">
                              <AlertTriangle className="h-3 w-3" />
                              Audio too long
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Audio is {scene.audio_duration.toFixed(1)}s but scene is {scene.duration.toFixed(1)}s — shorten the dialogue to fix.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  {isEditingDialogue ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedDialogue}
                        onChange={(e) => setEditedDialogue(e.target.value)}
                        placeholder="Enter the dialogue or narration for this scene..."
                        className="min-h-[100px]"
                      />
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => handleSaveDialogue(scene)}
                          disabled={isRegeneratingAudio || isShorteningDialogue}
                        >
                          {isRegeneratingAudio ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Updating...
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                        {scene.audio_duration && scene.audio_duration > getAudioTargetDuration(scene) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleShortenDialogue(scene)}
                            disabled={isShorteningDialogue || isRegeneratingAudio}
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          >
                            {isShorteningDialogue ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Shortening...
                              </>
                            ) : (
                              <>
                                <Scissors className="mr-2 h-4 w-4" />
                                Shorten
                              </>
                            )}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isRegeneratingAudio || isShorteningDialogue}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <p className="text-xs md:text-sm text-gray-600 line-clamp-3 md:line-clamp-none break-words">{scene.dialogue}</p>
                      {scene.audio_duration && scene.audio_duration > getAudioTargetDuration(scene) && !isContentLocked() && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleEditDialogue(scene);
                          }}
                          className="text-amber-600 border-amber-300 hover:bg-amber-50 h-7 text-xs"
                        >
                          <Scissors className="mr-1.5 h-3 w-3" />
                          Shorten dialogue
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Motion info strip — shows auto-selected template and re-render button */}
        {(scene.resolved_visual_type === 'motion' || scene.visual_type === 'motion') && !isContentLocked() && (
          <div className="mt-3 flex items-center justify-between border border-dashed border-indigo-200 rounded-lg px-3 py-2 bg-indigo-50/40">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-indigo-700">Remotion</span>
              {scene.visual_metadata?.template && (
                <span className="text-xs text-indigo-500 bg-indigo-100 px-1.5 py-0.5 rounded">
                  {scene.visual_metadata.template as string}
                </span>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleRenderMotion(scene)}
              disabled={regeneratingSceneIds.includes(scene.id)}
              className="h-6 px-2 text-xs text-indigo-600 hover:bg-indigo-100"
            >
              {regeneratingSceneIds.includes(scene.id) ? (
                <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Rendering...</>
              ) : (
                'Re-render'
              )}
            </Button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-3">
          {/* Mobile Dropdown */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between h-10">
                  Scene Actions
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuItem onClick={() => handlePreview(scene)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Scene
                </DropdownMenuItem>
                {!isUpload && (
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedSceneIds([scene.id]);
                    setShowTweakDialog(true);
                  }}
                  disabled={isContentLocked()}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Tweak Scene
                </DropdownMenuItem>
                )}
                {!isUpload && (
                <DropdownMenuItem
                  onClick={() => handleEditVisual(scene)}
                  disabled={isContentLocked() || editingState.sceneId === scene.id}
                >
                  <Pen className="h-4 w-4 mr-2" />
                  Edit Visual
                </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => handleEditDialogue(scene)}
                  disabled={isContentLocked() || editingState.sceneId === scene.id}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Edit Dialogue
                </DropdownMenuItem>
                {scene.audio_url && (
                  <DropdownMenuItem onClick={() => handleDownloadAudio(scene)}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Audio
                  </DropdownMenuItem>
                )}
                {scene.visual_url && scene.media_type === 'video' && (
                  <DropdownMenuItem onClick={() => handleDownloadVideo(scene)}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Video
                  </DropdownMenuItem>
                )}
                {!isUpload && (
                <DropdownMenuItem
                  onClick={() => handleRegenerateMedia(scene)}
                  disabled={!['draft', 'failed', 'completed'].includes(scene.status) ||
                    regeneratingSceneIds.includes(scene.id) ||
                    isContentLocked()}
                >
                  {regeneratingSceneIds.includes(scene.id) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RotateCw className="h-4 w-4 mr-2" />
                      Regenerate Media
                    </>
                  )}
                </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePreview(scene)}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
            {!isUpload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedSceneIds([scene.id]);
                setShowTweakDialog(true);
              }}
              disabled={isContentLocked()}
              className="flex items-center gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              Tweak
            </Button>
            )}
            {!isUpload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditVisual(scene)}
              disabled={isContentLocked() || editingState.sceneId === scene.id}
              className="flex items-center gap-2"
            >
              <Pen className="h-4 w-4" />
              Edit Visual
            </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditDialogue(scene)}
              disabled={isContentLocked() || editingState.sceneId === scene.id}
              className="flex items-center gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              Edit Dialogue
            </Button>
            {scene.audio_url && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadAudio(scene)}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Audio
              </Button>
            )}
            {scene.visual_url && scene.media_type === 'video' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadVideo(scene)}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Video
              </Button>
            )}
            {!isUpload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRegenerateMedia(scene)}
              disabled={!['draft', 'failed', 'completed'].includes(scene.status) ||
                regeneratingSceneIds.includes(scene.id) ||
                isContentLocked()}
              className="flex items-center gap-2"
            >
              {regeneratingSceneIds.includes(scene.id) ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RotateCw className="h-4 w-4" />
                  Regenerate Media
                </>
              )}
            </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  if (error) {
    console.error('Error fetching storyboard:', error);
    return (
      <EmptyState
        title="Error loading storyboard"
        description="There was a problem loading the storyboard. Please try again."
        action={
          <Button onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!storyboard || !storyboard.scenes || storyboard.scenes.length === 0) {
    return (
      <div className="container py-10">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        <Card className="p-8 max-w-2xl mx-auto">
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>Storyboard View</span>
              <span>•</span>
              <span>{storyboard?.title || 'Untitled'}</span>
            </div>

            <div className="space-y-4">
              <div className="p-3 bg-primary/5 rounded-full w-fit mx-auto">
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold">
                Generating Storyboard
              </h2>
              <p className="text-muted-foreground">
                We're creating your storyboard. This process usually takes a couple of minutes.
              </p>
              <div className="flex justify-center pt-4">
                <Button onClick={() => window.location.reload()}>
                  <RotateCw className="mr-2 h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const renderCombineConfirmDialog = () => (
    <Dialog open={showCombineConfirm} onOpenChange={setShowCombineConfirm}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Final Video</DialogTitle>
          <DialogDescription>
            Are you ready to combine your scenes into the final video? This process:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Cannot be interrupted once started</li>
              <li>May take several minutes to complete</li>
              <li>Will notify you when finished</li>
            </ul>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowCombineConfirm(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCombineScenes}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting Process...
              </>
            ) : (
              <>
                <FilmIcon className="mr-2 h-4 w-4" />
                Start Processing
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderApprovalDialog = () => (
    <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve Content Creation</DialogTitle>
          <DialogDescription className="space-y-4">
            {isCalculatingCost ? (
              "Calculating credit cost..."
            ) : creditCostInfo ? (
              <>
                <p>
                  This will use <span className="font-semibold text-primary">{creditCostInfo.creditCost}</span> credits from your account. Are you sure you want to proceed?
                </p>
                <div className="rounded-md bg-muted p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <FilmIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Generating content for </span>
                    <span className="font-medium">{creditCostInfo.scenes} scenes</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Using </span>
                    <span className="font-medium">{creditCostInfo.visualModel}</span>
                    <span className="text-muted-foreground"> model</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Coins className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Total cost: </span>
                    <span className="font-medium">{creditCostInfo.creditCost} credits</span>
                  </div>
                </div>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Content will be generated for all scenes</li>
                  <li>You can still make adjustments after approval</li>
                  <li>Credits will be deducted upon approval</li>
                </ul>
              </>
            ) : (
              "Unable to calculate credit cost"
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 mt-4">
          <Button 
            variant="outline" 
            onClick={() => setShowApprovalDialog(false)}
            disabled={isApproving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveContent}
            disabled={isApproving || !creditCostInfo || isCalculatingCost}
            className="min-w-[200px]"
          >
            {isApproving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Approving...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve & Start Creation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderTweakDialog = () => (
    <Dialog open={showTweakDialog} onOpenChange={(open) => {
      if (!open) handleCancelTweak();
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tweak {selectedSceneIds.length > 0 ? `${selectedSceneIds.length} Scene${selectedSceneIds.length > 1 ? 's' : ''}` : 'Scene'}</DialogTitle>
          <DialogDescription>
            Describe how you'd like to modify the scene{selectedSceneIds.length > 1 ? 's' : ''}. You can reference both visual and audio aspects.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Textarea
            value={tweakFeedback}
            onChange={(e) => setTweakFeedback(e.target.value)}
            placeholder="Example: Make the scene more dynamic with faster pacing. Add more vibrant colors and energetic music."
            className="min-h-[120px]"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancelTweak}
            disabled={isTweaking}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTweakScenes}
            disabled={isTweaking || !tweakFeedback.trim()}
          >
            {isTweaking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Apply Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderRegenerateDialog = () => (
    <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Regenerate Scenes</DialogTitle>
          <DialogDescription>
            {selectedSceneIds.length === 0 ? (
              "Select scenes to regenerate"
            ) : (
              `Regenerating ${selectedSceneIds.length} scene${selectedSceneIds.length > 1 ? 's' : ''}`
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Textarea
            value={regenerateText}
            onChange={(e) => setRegenerateText(e.target.value)}
            placeholder="Describe how you want to change these scenes..."
            className="min-h-[120px]"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowRegenerateDialog(false);
              setSelectedSceneIds([]);
            }}
            disabled={isRegenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerateScenes}
            disabled={isRegenerating || selectedSceneIds.length === 0 || !regenerateText.trim()}
          >
            {isRegenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Regenerating...
              </>
            ) : (
              'Regenerate Scenes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <TooltipProvider delayDuration={0}>
      <div className="container max-w-6xl py-4 md:py-10 thin-scrollbar">
        {/* Video Preview Card */}
        <div className="w-full md:w-[120%] md:-ml-[10%] mb-6 md:mb-8">
          <Card className="overflow-hidden">
            <div className="flex flex-col">
              {canCreateFinalVideo() && (
                <div className="bg-amber-50 border-b border-amber-100 px-4 py-3 md:p-4 flex items-start md:items-center gap-3 rounded-t-lg">
                  <AlertCircle className="h-4 w-4 md:h-5 md:w-5 mt-0.5 md:mt-0 text-amber-500 shrink-0" />
                  <div>
                    <h3 className="hidden md:block font-medium text-amber-800">Ready to Create Final Video</h3>
                    <p className="text-xs md:text-sm text-amber-700">
                      <span className="md:hidden">All scenes have been generated. Ready to create your final video.</span>
                      <span className="hidden md:inline">All scenes have been generated. You can preview and tweak individual scenes before creating your final video.</span>
                    </p>
                  </div>
                </div>
              )}
              {getContentStatus() === 'processing' && (
                <div className="bg-blue-50 border-b border-blue-100 px-4 py-3 md:p-4 flex items-start md:items-center gap-3 rounded-t-lg">
                  <Loader2 className="h-4 w-4 md:h-5 md:w-5 mt-0.5 md:mt-0 text-blue-500 animate-spin shrink-0" />
                  <div>
                    <h3 className="hidden md:block font-medium text-blue-800">Processing Video</h3>
                    <p className="text-xs md:text-sm text-blue-700">
                      <span className="md:hidden">Your scenes are being combined into a final video.</span>
                      <span className="hidden md:inline">Your scenes are being combined into a final video. This may take a few minutes.</span>
                    </p>
                  </div>
                </div>
              )}
              <div className="p-4 md:p-6">
                <div className="md:flex md:justify-center">
                  {(() => {
                    const isPortrait = ['tiktok', 'instagram', 'shorts'].includes(storyboard.platform?.toLowerCase() ?? '');
                    return (
                  <div className={`relative w-full bg-black rounded-lg overflow-hidden ${isPortrait ? 'mx-auto' : ''}`} style={{ maxWidth: isPortrait ? '360px' : '1024px' }}>
                    <div className={isPortrait ? 'aspect-[9/16]' : 'aspect-video'}>
                      {storyboard.video_url ? (
                        <video
                          src={storyboard.video_url}
                          controls
                          className="absolute inset-0 w-full h-full object-contain"
                          poster={storyboard.thumbnail_url}
                          onError={handleVideoError}
                          onLoadedData={handleVideoLoad}
                          onPlay={stopActiveAudio}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center flex-col text-muted-foreground p-4">
                          <FilmIcon className="h-8 w-8 md:h-12 md:w-12 mb-2 opacity-20" />
                          <p className="text-xs md:text-base">Final video not yet available</p>
                          {getContentStatus() === 'processing' && (
                            <div className="mt-3 md:mt-4 flex items-center gap-2">
                              <Loader2 className="h-3 w-3 md:h-4 md:w-4 animate-spin" />
                              <p className="text-xs md:text-sm">Processing video...</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                    );
                  })()}
                </div>
                {storyboard.video_url && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      type="button"
                      onClick={handleDownloadFinalVideo}
                      className="w-full sm:w-auto"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Video
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Header Section */}
        <div className="w-full md:w-[120%] md:-ml-[10%] mb-6 md:mb-8">
          <div className="px-1 md:px-0 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-muted-foreground h-8 w-8 md:h-9 md:w-9 -ml-2 md:ml-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-0.5 md:space-y-0 md:flex md:items-baseline md:gap-2">
                <h1 className="text-base md:text-xl font-medium text-foreground/90">Scene Breakdown</h1>
                <span className="text-xs md:text-sm text-muted-foreground font-medium">{storyboard.title}</span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Total Duration: {totalDuration}s</span>
              </div>

              {selectedSceneIds.length > 1 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowTweakDialog(true)}
                  className="h-9"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Tweak Selected ({selectedSceneIds.length})
                </Button>
              )}

              {scenesNeedingAudioShortening.length > 0 && !isContentLocked() && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleShortenAllAudio}
                  disabled={isShorteningAllAudio}
                  className="h-9 text-xs md:text-sm w-full md:w-auto text-amber-600 border-amber-300 hover:bg-amber-50"
                >
                  {isShorteningAllAudio ? (
                    <>
                      <Loader2 className="mr-1.5 md:mr-2 h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                      Shortening...
                    </>
                  ) : (
                    <>
                      <Scissors className="mr-1.5 md:mr-2 h-3.5 w-3.5 md:h-4 md:w-4" />
                      Shorten All ({scenesNeedingAudioShortening.length})
                    </>
                  )}
                </Button>
              )}

              {/* Approve only while draft and scene media is not all ready (avoids duplicate path vs Create Final Video) */}
              {getContentStatus() === 'draft' && !areAllScenesMediaComplete() && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      onClick={handleCalculateCreditCost}
                      disabled={isCalculatingCost}
                      className="h-9 text-xs md:text-sm w-full md:w-auto"
                    >
                      {isCalculatingCost ? (
                        <>
                          <Loader2 className="mr-1.5 md:mr-2 h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                          <span className="md:hidden">Calculating...</span>
                          <span className="hidden md:inline">Calculating Credits...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-1.5 md:mr-2 h-3.5 w-3.5 md:h-4 md:w-4" />
                          <span className="md:hidden">Approve</span>
                          <span className="hidden md:inline">Approve Content</span>
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs md:text-sm">Approve and start content creation</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* Create final video when server is awaiting compilation, or when every scene is ready but content status lags */}
              {canCreateFinalVideo() && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      size="sm"
                      onClick={() => setShowCombineConfirm(true)}
                      disabled={isProcessing}
                      className="h-9 text-xs md:text-sm w-full md:w-auto"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-1.5 md:mr-2 h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                          <span className="md:hidden">Processing...</span>
                          <span className="hidden md:inline">Processing Video...</span>
                        </>
                      ) : (
                        <>
                          <FilmIcon className="mr-1.5 md:mr-2 h-3.5 w-3.5 md:h-4 md:w-4" />
                          <span className="md:hidden">Create Video</span>
                          <span className="hidden md:inline">Create Final Video</span>
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs md:text-sm">Create final video by combining all scenes</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Lock banner */}
        {isContentLocked() && (
          <div className="w-full md:w-[120%] md:-ml-[10%] mb-4">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/60 border border-border rounded-lg text-sm text-muted-foreground">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              {getContentStatus() === 'completed'
                ? 'This content is completed — editing is disabled.'
                : 'This content is being processed — editing is disabled until complete.'}
            </div>
          </div>
        )}

        {/* Scenes List */}
        <div className="w-full md:w-[120%] md:-ml-[10%] space-y-4 md:space-y-6">
          {storyboard.scenes.map((scene: BackendScene) => renderScene(scene))}

          {/* Add New Scene Button */}
          {!isContentLocked() && (
            <div className="mt-6 md:mt-8">
              <Button
                variant="default"
                onClick={() => setShowNewSceneDialog(true)}
                className="w-full bg-primary hover:bg-primary/90 py-6 md:py-4"
                size="lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Scene
              </Button>
            </div>
          )}
        </div>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="p-4 md:p-6 max-w-4xl">
            <DialogHeader>
              <DialogTitle className="text-base md:text-xl">
                Scene Preview
              </DialogTitle>
              <DialogDescription className="hidden md:block">
                {selectedScene?.media_type === 'video' ? 'Video Content' : 'Image Content'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 md:space-y-4">
              {selectedScene?.visual_url ? (
                <>
                  {(() => {
                    const isPortrait = ['tiktok', 'instagram', 'shorts'].includes(storyboard?.platform?.toLowerCase() ?? '');
                    return (
                      <div className="flex justify-center">
                        <div
                          className={`relative bg-black rounded-lg overflow-hidden ${isPortrait ? 'aspect-[9/16] max-w-[240px] w-full' : 'aspect-video w-full'}`}
                          style={{ maxHeight: '60vh' }}
                        >
                          {selectedScene.media_type === 'video' ? (
                            <video
                              src={selectedScene.visual_url}
                              controls
                              className="w-full h-full object-contain"
                              playsInline
                              controlsList="nodownload"
                              onError={handleVideoError}
                              onLoadedData={handleVideoLoad}
                              onPlay={stopActiveAudio}
                              key={`video-${selectedScene.id}-${selectedScene.visual_url}`}
                            />
                          ) : (() => {
                            // Frame the image the way compile will: cover-crop by
                            // default, or fit over a blurred bg when fit=contain.
                            const fit =
                              (selectedScene.visual_metadata as { fit?: string } | undefined)?.fit ?? 'cover';
                            return (
                              <>
                                {fit === 'contain' && (
                                  <img
                                    src={selectedScene.visual_url}
                                    aria-hidden
                                    className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-60"
                                  />
                                )}
                                <img
                                  src={selectedScene.visual_url}
                                  alt={selectedScene.description}
                                  className={`relative w-full h-full ${fit === 'contain' ? 'object-contain' : 'object-cover'}`}
                                />
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                  {selectedScene.media_type === 'video' && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground italic">
                        Note: Video saving is currently only supported on desktop/laptop browsers
                      </p>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadVideo(selectedScene)}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download Video
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-gray-100 rounded-lg p-6 md:p-8 text-center">
                  <p className="text-xs md:text-base text-muted-foreground">Preview not available</p>
                </div>
              )}
              
              {selectedScene?.audio_url && (
                <div className="space-y-2">
                  <CustomAudioPlayer
                    audioUrl={`${selectedScene.audio_url}${selectedScene.audio_url.includes('?') ? '&' : '?'}t=${selectedScene.audio_updated_at}`}
                    showMilliseconds={true}
                  />
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadAudio(selectedScene)}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download Audio
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Collapsible className="border border-border rounded-md" defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 hover:bg-secondary/50">
                    <h4 className="text-xs font-medium">
                      {selectedScene && isStockScene(selectedScene) ? 'Search Query' : 'Visual Description'}
                    </h4>
                    <div className="h-5 w-5 flex items-center justify-center">
                      <ChevronDown className="h-4 w-4 transition-transform collapsible-open:rotate-180" />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-3 border-t border-border">
                    <p className="text-xs md:text-sm">
                      {selectedScene
                        ? isStockScene(selectedScene)
                          ? (selectedScene.search_keywords || selectedScene.description)
                          : selectedScene.description
                        : ''}
                    </p>
                  </CollapsibleContent>
                </Collapsible>
                
                <Collapsible className="border border-border rounded-md" defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 hover:bg-secondary/50">
                    <h4 className="text-xs font-medium">Dialogue/Narration</h4>
                    <div className="h-5 w-5 flex items-center justify-center">
                      <ChevronDown className="h-4 w-4 transition-transform collapsible-open:rotate-180" />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-3 border-t border-border">
                    <p className="text-xs md:text-sm">{selectedScene?.dialogue || "No dialogue"}</p>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Combine Confirm Dialog */}
        {renderCombineConfirmDialog()}

        {/* Add the approval dialog */}
        {renderApprovalDialog()}

        {/* Add the tweak dialog */}
        {renderTweakDialog()}

        {/* Add the regenerate dialog */}
        {renderRegenerateDialog()}

        {/* Delete Scene Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Scene {sceneToDelete?.number}?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the scene and its content.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingScene}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => sceneToDelete && handleDeleteScene(sceneToDelete)}
                disabled={isDeletingScene}
                className="bg-destructive hover:bg-destructive/90"
              >
                {isDeletingScene ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Scene'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Generate New Scene Dialog */}
        <Dialog open={showNewSceneDialog} onOpenChange={setShowNewSceneDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Generate New Scene</DialogTitle>
              <DialogDescription>
                Add a new scene to your storyboard. You can optionally specify where to add it and what it should be about.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Position Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Scene Position</label>
                <Select
                  value={newScenePosition?.toString() || ""}
                  onValueChange={(value) => setNewScenePosition(value ? parseInt(value) : null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Add as last scene" />
                  </SelectTrigger>
                  <SelectContent>
                    {storyboard?.scenes.map((_, idx) => (
                      <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                        Insert at Scene {idx + 1}
                      </SelectItem>
                    ))}
                    <SelectItem value={(storyboard?.scenes.length + 1).toString()}>
                      Add as last scene
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Show Context of Surrounding Scenes */}
              {newScenePosition && (
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-muted-foreground">Surrounding Context:</p>
                  {newScenePosition > 1 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium">Scene {newScenePosition - 1}:</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {storyboard?.scenes[newScenePosition - 2]?.description || 'No previous scene'}
                      </p>
                    </div>
                  )}
                  {newScenePosition <= storyboard?.scenes.length && (
                    <div className="space-y-1 mt-2">
                      <p className="text-xs font-medium">Scene {newScenePosition + 1}:</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {storyboard?.scenes[newScenePosition - 1]?.description || 'No next scene'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Scene Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Scene Description (Optional)</label>
                <Textarea
                  value={newSceneDescription}
                  onChange={(e) => setNewSceneDescription(e.target.value)}
                  placeholder="Describe what should happen in this scene..."
                  className="min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to automatically extend the previous scene's action
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setNewScenePosition(null);
                  setNewSceneDescription("");
                  setShowNewSceneDialog(false);
                }}
                disabled={isGeneratingScene}
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerateNewScene}
                disabled={isGeneratingScene}
              >
                {isGeneratingScene ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Scene'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add the regenerate media confirmation dialog */}
        <Dialog 
          open={isRegenerateMediaDialogOpen} 
          onOpenChange={setIsRegenerateMediaDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Media Regeneration</DialogTitle>
              <DialogDescription className="space-y-4">
                {isCalculatingRegenerateCost ? (
                  "Calculating credit cost..."
                ) : regenerateCreditInfo ? (
                  <>
                    <p>
                      This will use <span className="font-semibold text-primary">{regenerateCreditInfo.creditCost}</span> credits from your account. Are you sure you want to proceed?
                    </p>
                    <div className="rounded-md bg-muted p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <FilmIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Regenerating media for scene </span>
                        <span className="font-medium">{sceneToRegenerate?.number}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Sparkles className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Using </span>
                        <span className="font-medium">{regenerateCreditInfo.visualModel}</span>
                        <span className="text-muted-foreground"> model</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Coins className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Total cost: </span>
                        <span className="font-medium">{regenerateCreditInfo.creditCost} credits</span>
                      </div>
                    </div>
                  </>
                ) : (
                  "Regenerating media for this scene will use credits from your account. Would you like to proceed?"
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsRegenerateMediaDialogOpen(false);
                  setSceneToRegenerate(null);
                  setRegenerateCreditInfo(null);
                }}
                disabled={sceneToRegenerate ? regeneratingSceneIds.includes(sceneToRegenerate.id) : false}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRegenerate}
                disabled={!sceneToRegenerate || 
                  regeneratingSceneIds.includes(sceneToRegenerate.id) ||
                  isCalculatingRegenerateCost}
              >
                {regeneratingSceneIds.includes(sceneToRegenerate?.id || -1) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Regenerating...
                  </>
                ) : isCalculatingRegenerateCost ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
